import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import "../../css/List.css";
import "../../css/TransactionsHistory.css";
import TransactionHistoryController from "../../../controller/Transaction/transactionHistoryController";
import { TransactionType, TransactionStatus } from "../../../utils/enums";
import TransactionDetailsController from "../../../controller/Transaction/transactionDetailsController";
import { parseLocalDateFromYmd } from "../../../utils/dateRangeApiFormat";
import { formatApiDateTime } from "../../../utils/dateTime";
import { downloadStatementOfAccountPdf, emailStatementOfAccountPdf } from "../../../utils/statementOfAccountPdf";
import { getPermissionControllers, isAccountPermissionDisabled } from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import { useToast } from "../../../contexts/ToastContext";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import TransactionDetailsModal from "../../../components/TransactionDetailsModal";
import {
  MFG_FCM_NAV_TARGET_KEY,
  MFG_FCM_NAV_TX_KEY,
} from "../../../services/fcmNotificationHandler";

const TransactionsHistory = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const { error: showError, success: showSuccess } = useToast();
  const [user, setUser] = useState(null);
  const [typeFilter, setTypeFilter] = useState(TransactionType.ALL);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [perPage] = useState(10);
  const [pagination, setPagination] = useState({ page: 0, perPage: 10, total: 0, lastPage: 0 });
  const [detailsTx, setDetailsTx] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isGatewayOpen, setIsGatewayOpen] = useState(false);
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [gatewayError, setGatewayError] = useState(null);
  const [gatewayDocuments, setGatewayDocuments] = useState([]);
  const [gatewayTx, setGatewayTx] = useState(null);
  const [docActionVisibility, setDocActionVisibility] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [dateRange, setDateRange] = useState({
    dateFrom: null,
    dateTo: null,
    presetId: null,
  });
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [dateFieldErrors, setDateFieldErrors] = useState({ dateFrom: false, dateTo: false });
  const [isProcessingStatement, setIsProcessingStatement] = useState(false);
  const [showStatementPassword, setShowStatementPassword] = useState(false);
  const [statementAction, setStatementAction] = useState(null);
  const loadRequestIdRef = useRef(0);
  const actionsMenuRef = useRef(null);

  useEffect(() => {
    if (!actionsMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!actionsMenuRef.current?.contains(event.target)) {
        setActionsMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setActionsMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [actionsMenuOpen]);

  const transactionTypeLabels = {
    [TransactionType.INTERNAL_TRANSFER]: t("internal_transfer", { ns: "mobile" }),
    [TransactionType.ADMIN_FEES]: t("admin_fees", { ns: "mobile" }),
    [TransactionType.GENERATE]: t("generate", { ns: "mobile" }),
    [TransactionType.DEPOSIT]: t("deposit", { ns: "mobile" }),
    [TransactionType.WITHDRAWAL]: t("withdrawal", { ns: "mobile" }),
    [TransactionType.CASHIN]: t("cashin", { ns: "mobile" }),
    [TransactionType.CASHOUT]: t("cashout", { ns: "mobile" }),
    [TransactionType.COMMISSION]: t("commission", { ns: "mobile" }),
    [TransactionType.CREDIT_IN]: t("credit_in", { ns: "mobile" }),
    [TransactionType.CREDIT_OUT]: t("credit_out", { ns: "mobile" }),
    [TransactionType.EXCHANGE]: t("exchange", { ns: "mobile" }),
    [TransactionType.REFUND]: t("refund", { ns: "mobile" }),
    [TransactionType.PAY_BY_CARD]: t("pay_with_card", { ns: "mobile" }),
    [TransactionType.RESET_WALLET]: t("reset_wallet", { ns: "mobile" }),
    [TransactionType.RESET_BALANCE_MT5]: t("reset_mt5_balance", { ns: "mobile" }),
    [TransactionType.COLLECTED]: t("collected", { ns: "mobile" }),
    [TransactionType.REFUND_COLLECTED]: t("refund_collect", { ns: "mobile" }),
    [TransactionType.TRANSFER]: t("transfer", { ns: "mobile" }),
    [TransactionType.CASHIN_MT5]: t("cashin", { ns: "mobile" }),
    [TransactionType.BALANCE_REPORT]: t("balance", { ns: "mobile" }),
  };

  const transactionStatusLabels = {
    [TransactionStatus.STARTED]: t("started", { ns: "mobile" }),
    [TransactionStatus.PENDING]: t("pending", { ns: "mobile" }),
    [TransactionStatus.FAILED]: t("failed", { ns: "mobile" }),
    [TransactionStatus.COMPLETED]: t("completed", { ns: "mobile" }),
    [TransactionStatus.REJECTED]: t("rejected", { ns: "mobile" }),
    [TransactionStatus.CANCELED]: t("canceled", { ns: "mobile" }),
    [TransactionStatus.PROGRESSING]: t("waiting", { ns: "mobile" }),
    [TransactionStatus.PENDING_USER]: t("pending", { ns: "mobile" }),
    [TransactionStatus.COLLECTED]: t("collected", { ns: "mobile" }),
    [TransactionStatus.TRANSFERRED]: t("transferred", { ns: "mobile" }),
  };

  const mapTransactionWordLabel = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return null;
    const normalized = raw.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
    if (normalized === "cash in" || normalized === "cashin") return t("cashin", { ns: "mobile" });
    if (normalized === "cash out" || normalized === "cashout") return t("cashout", { ns: "mobile" });
    if (normalized === "deposit") return t("deposit", { ns: "mobile" });
    if (normalized === "withdraw" || normalized === "withdrawal") return t("withdrawal", { ns: "mobile" });
    if (normalized === "internal transfer" || normalized === "internaltransfer") {
      return t("internal_transfer", { ns: "mobile" });
    }
    return null;
  };

  // Load basic user data for Sidebar (same pattern as other pages that read from storage)
  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData") || localStorage.getItem("userData");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (_) {
      setUser(null);
    }
  }, []);

  const canDownloadHistoryPdf = useMemo(() => {
    if (!user) return false;
    const { customerPerm } = getPermissionControllers(user);
    return !isAccountPermissionDisabled(
      customerPerm,
      PermissionCustomerModelController.PER__CAN_DOWNLOAD_HISTORY_PDF
    );
  }, [user]);

  const loadTransactions = async (overrides = {}) => {
    const requestId = ++loadRequestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const requestParams = {
        page: overrides.page !== undefined ? overrides.page : page,
        perPage,
        type: overrides.type !== undefined ? overrides.type : typeFilter,
        dateFrom:
          overrides.dateFrom !== undefined ? overrides.dateFrom : dateRange.dateFrom,
        dateTo: overrides.dateTo !== undefined ? overrides.dateTo : dateRange.dateTo,
        search:
          overrides.search !== undefined ? overrides.search : searchTerm || null,
        status: null, // null = all statuses (history shows all transactions)
      };
      
      const result = await TransactionHistoryController.getAllTransactions(requestParams);
      if (requestId !== loadRequestIdRef.current) return;

      // Prefer normalized items from controller, but if empty fall back to raw.DATA.data
      const rawDataBlock = result?.raw?.DATA || {};
      const rawList = Array.isArray(rawDataBlock.data) ? rawDataBlock.data : [];

      const sourceList =
        result.items && result.items.length ? result.items : rawList;

      const normalized = sourceList.map((item) => {
        const rawItem = item.raw || item;

        const id =
          rawItem.TRANSACTION_ID ||
          rawItem.REF_ID ||
          rawItem.REFID ||
          rawItem.REFERENCE_ID ||
          rawItem.ID ||
          rawItem.id ||
          null;

        const transactionId =
          rawItem.TRANSACTION_ID ||
          rawItem.transaction_id ||
          null;
        const requestToken =
          rawItem.REQUEST_TOKEN ||
          rawItem.request_token ||
          rawItem.requestToken ||
          null;

        const date =
          rawItem.TRANSACTION_DATE ||
          rawItem.TRX_DATE ||
          rawItem.CREATED_AT ||
          rawItem.DATE ||
          rawItem.date ||
          null;

        const amount =
          rawItem.AMOUNT ??
          rawItem.amount ??
          rawItem.VALUE ??
          null;

        const balanceSnapshot =
          rawItem.BALANCE_SNAPSHOT ??
          rawItem.balanceSnapshot ??
          null;

        let balanceAfter =
          rawItem.BALANCE_AFTER ??
          rawItem.BALANCE ??
          rawItem.CURRENT_BALANCE ??
          rawItem.balance ??
          null;

        // Fallback: if no direct balance field but we have a balance snapshot map
        // Flutter logic: match user's wallet ID from sender/receiver wallet
        if (
          balanceAfter == null &&
          balanceSnapshot &&
          typeof balanceSnapshot === "object"
        ) {
          // Try to find the current user's wallet balance (like Flutter does)
          const storedUserData = sessionStorage.getItem("userData") || localStorage.getItem("userData");
          let currentUserId = null;
          try {
            if (storedUserData) {
              const parsedUser = JSON.parse(storedUserData);
              currentUserId = parsedUser?.id || parsedUser?.ID || parsedUser?.user?.id;
            }
          } catch (_) {}

          // Collect possible wallet IDs belonging to current user
          const possibleWalletIds = [];
          const senderWallet = rawItem.sender_wallet || rawItem.SENDER_WALLET;
          const receiverWallet = rawItem.receiver_wallet || rawItem.RECEIVER_WALLET;

          if (senderWallet?.account?.user?.id === currentUserId || 
              senderWallet?.account?.user?.ID === currentUserId) {
            possibleWalletIds.push(senderWallet.id || senderWallet.ID);
          }
          if (receiverWallet?.account?.user?.id === currentUserId || 
              receiverWallet?.account?.user?.ID === currentUserId) {
            possibleWalletIds.push(receiverWallet.id || receiverWallet.ID);
          }

          // If no wallet matched, use all snapshot keys as fallback
          if (possibleWalletIds.length === 0) {
            possibleWalletIds.push(...Object.keys(balanceSnapshot));
          }

          // Find balance for matching wallet ID
          for (const walletId of possibleWalletIds) {
            const balanceValue = 
              balanceSnapshot[walletId] ?? 
              balanceSnapshot[String(walletId)] ?? 
              (typeof walletId === "string" ? balanceSnapshot[parseInt(walletId, 10)] : null);
            
            if (balanceValue != null) {
              balanceAfter = balanceValue;
              break;
            }
          }
        }

        const typeCode =
          rawItem.TYPE ??
          rawItem.type ??
          null;

        const rawStatus =
          rawItem.STATUS ??
          rawItem.status ??
          null;

        const statusCode =
          typeof rawStatus === "number" ? rawStatus : null;

        const statusLabelFromApi =
          rawItem.STATUS_LABEL ||
          rawItem.STATUS_NAME ||
          (typeof rawStatus === "string" ? rawStatus : null);

        const translatedStatusLabelFromApi = mapTransactionWordLabel(statusLabelFromApi);

        const statusLabel =
          translatedStatusLabelFromApi ||
          statusLabelFromApi ||
          (typeof statusCode === "number" && transactionStatusLabels[statusCode]) ||
          null;

        const typeLabelFromApi =
          rawItem.TYPE_LABEL ||
          rawItem.TYPE_NAME ||
          (typeof typeCode === "string" ? typeCode : null);
        const translatedTypeLabelFromApi = mapTransactionWordLabel(typeLabelFromApi);

        const typeLabel =
          (typeof typeCode === "number" && transactionTypeLabels[typeCode]) ||
          translatedTypeLabelFromApi ||
          typeLabelFromApi ||
          t("transaction", { ns: "mobile", defaultValue: "Transaction" });

        let typeClass = "type-unknown";
        if (typeCode === TransactionType.CASHIN || typeCode === TransactionType.CASHIN_MT5) {
          typeClass = "type-cashin";
        } else if (typeCode === TransactionType.CASHOUT) {
          typeClass = "type-cashout";
        } else if (typeCode === TransactionType.INTERNAL_TRANSFER || typeCode === TransactionType.TRANSFER) {
          typeClass = "type-transfer";
        } else {
          typeClass = "type-real";
        }

        return {
          id,
          transactionId,
          requestToken,
          date,
          amount,
          balanceAfter,
          statusLabel,
          statusCode,
          typeCode,
          typeLabel,
          typeClass,
          raw: rawItem,
        };
      });

      setTransactions(normalized);

      const currentPage = Number(rawDataBlock.current_page ?? (result.pagination?.page ?? 0) + 1);
      const lastPage = Number(rawDataBlock.last_page ?? (result.pagination?.lastPage ?? 0) + 1);
      const perPageVal = Number(rawDataBlock.per_page ?? result.pagination?.perPage ?? perPage) || perPage;
      const totalVal = Number(rawDataBlock.total ?? result.pagination?.total ?? 0);

      setPagination({
        page: currentPage - 1,
        perPage: perPageVal,
        total: totalVal,
        lastPage: lastPage - 1,
      });
    } catch (e) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(e.message || "Failed to load transactions");
      setTransactions([]);
      console.error("[Transactions] Failed to load transactions", e);
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, page]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const cashinRows = transactions.filter((tx) => isCashinType(tx));
      if (cashinRows.length === 0) {
        if (!cancelled) setDocActionVisibility({});
        return;
      }
      const nextVisibility = {};
      await Promise.all(
        cashinRows.map(async (tx) => {
          const key = historyDocKey(tx);
          const txId = tx?.transactionId || tx?.id;
          if (!key || !txId) return;
          try {
            const byTx =
              await TransactionHistoryController.getCashinGatewayInformation(txId);
            const docs = byTx?.documents || [];
            nextVisibility[key] = docs.length > 0;
          } catch (_) {
            nextVisibility[key] = false;
          }
        })
      );
      if (!cancelled) {
        setDocActionVisibility(nextVisibility);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions]);

  const applyFilters = () => {
    if (!requireDateRange()) return;
    setPage(0);
    TransactionHistoryController.clearCache();
    loadTransactions({
      page: 0,
      type: typeFilter,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
      search: searchTerm || null,
    });
  };

  const requireDateRange = () => {
    const missingFrom = !dateRange.dateFrom;
    const missingTo = !dateRange.dateTo;
    setDateFieldErrors({ dateFrom: missingFrom, dateTo: missingTo });
    if (missingFrom || missingTo) {
      setActionsMenuOpen(false);
      return false;
    }
    return true;
  };

  const sendStatementEmail = async () => {
    if (!canDownloadHistoryPdf) return;
    if (isProcessingStatement) return;
    setIsProcessingStatement(true);
    try {
      await emailStatementOfAccountPdf({
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        user,
      });
      showSuccess(
        t("email_sent", { ns: "mobile", defaultValue: "Email sent" }),
        t("statement_email_sent", {
          ns: "mobile",
          defaultValue: "Your Statement of Account has been sent to your email.",
        })
      );
    } catch (_) {
      showError(
        t("error", { ns: "mobile", defaultValue: "Error" }),
        t("email_failed", {
          ns: "mobile",
          defaultValue: "Could not send the statement email.",
        }),
        { skipLogout: true }
      );
    } finally {
      setIsProcessingStatement(false);
    }
  };

  const handleEmailAction = () => {
    if (!canDownloadHistoryPdf) return;
    if (!requireDateRange()) return;
    setActionsMenuOpen(false);
    if (isProcessingStatement) return;
    setStatementAction("email");
    setShowStatementPassword(true);
  };

  const handleDownloadAction = () => {
    if (!canDownloadHistoryPdf) return;
    if (!requireDateRange()) return;
    setActionsMenuOpen(false);
    if (isProcessingStatement) return;
    setStatementAction("download");
    setShowStatementPassword(true);
  };

  const handleStatementPasswordClose = async (result) => {
    const action = statementAction;
    setShowStatementPassword(false);
    setStatementAction(null);
    if (result !== "ok") return;
    if (action === "email") {
      await sendStatementEmail();
      return;
    }
    if (action !== "download") return;
    if (isProcessingStatement) return;
    setIsProcessingStatement(true);
    try {
      await downloadStatementOfAccountPdf({
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        user,
      });
    } catch (_) {
      showError(
        t("error", { ns: "mobile", defaultValue: "Error" }),
        t("download_failed", {
          ns: "mobile",
          defaultValue: "Could not download the statement.",
        }),
        { skipLogout: true }
      );
    } finally {
      setIsProcessingStatement(false);
    }
  };

  const showDownloadPassword = showStatementPassword;
  const handleDownloadPasswordClose = handleStatementPasswordClose;

  const canGoPrev = page > 0;
  const canGoNext =
    pagination.lastPage != null
      ? page < Number(pagination.lastPage)
      : transactions.length === perPage;

  const formatDateTime = (value) => formatApiDateTime(value);

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const toDateInputValue = (v) => {
    if (!v) return "";
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const openDetails = async (tx) => {
    if (!tx || !tx.id) {
      return;
    }
    setDetailsTx(tx);
    setDetailsData(null);
    setDetailsError(null);
    setIsDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const data = await TransactionDetailsController.getTransactionDetails(tx.id);
      setDetailsData(data);
    } catch (e) {
      setDetailsError(e.message || "Failed to load transaction details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setIsDetailsOpen(false);
    setDetailsTx(null);
    setDetailsData(null);
    setDetailsError(null);
  };

  const isCashinType = (tx) => Number(tx?.typeCode) === TransactionType.CASHIN;
  const historyDocKey = (tx) => String(tx?.transactionId || tx?.id || "");
  const shouldShowDocActions = (tx) => {
    const key = historyDocKey(tx);
    if (!key) return false;
    return docActionVisibility[key] !== false;
  };

  const openCashinGatewayPopup = async (tx) => {
    const txId = tx?.transactionId || tx?.id;
    if (!txId) return;
    const key = historyDocKey(tx);
    setGatewayTx(tx);
    setGatewayLoading(true);
    setGatewayError(null);
    setGatewayDocuments([]);
    setIsGatewayOpen(true);
    try {
      const byTx = await TransactionHistoryController.getCashinGatewayInformation(txId);
      const docs = byTx?.documents || [];
      setGatewayDocuments(docs);
    } catch (e) {
      setGatewayError(e.message || "Failed to load uploaded documents.");
    } finally {
      setGatewayLoading(false);
    }
  };

  const closeGatewayPopup = () => {
    setIsGatewayOpen(false);
    setGatewayLoading(false);
    setGatewayError(null);
    setGatewayDocuments([]);
    setGatewayTx(null);
  };

  const openGatewayDocument = async (filePath) => {
    try {
      const blob = await TransactionHistoryController.downloadCashinGatewayDocument(filePath);
      if (!(blob instanceof Blob)) {
        throw new Error("Invalid document payload.");
      }
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = String(filePath || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (e) {
      setGatewayError(e.message || "Failed to open document.");
    }
  };

  const quickDownloadCashinDocument = async (tx) => {
    const txId = tx?.transactionId || tx?.id;
    if (!txId) return;
    const key = historyDocKey(tx);
    try {
      const byTx = await TransactionHistoryController.getCashinGatewayInformation(txId);
      const firstDocument = Array.isArray(byTx?.documents) ? byTx.documents[0] : null;
      if (!firstDocument) {
        setGatewayError("No uploaded document found for this transaction.");
        return;
      }
      await openGatewayDocument(firstDocument);
    } catch (e) {
      setGatewayError(e.message || "Failed to download document.");
    }
  };

  useEffect(() => {
    try {
      const target = sessionStorage.getItem(MFG_FCM_NAV_TARGET_KEY);
      const txId = sessionStorage.getItem(MFG_FCM_NAV_TX_KEY);
      if (target !== "history" || !txId) return;
      sessionStorage.removeItem(MFG_FCM_NAV_TARGET_KEY);
      sessionStorage.removeItem(MFG_FCM_NAV_TX_KEY);
      openDetails({ id: txId, raw: {} });
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatAmountDisplay = (tx) => {
    const rawAmount = Number(tx?.amount ?? 0);
    if (!Number.isFinite(rawAmount)) return "-";
    const absAmount = Math.abs(rawAmount);
    const formatted = absAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const raw = tx?.raw || {};
    const symbol =
      raw.CURRENCY_SYMBOL ||
      raw.currencySymbol ||
      (raw.CURRENCY && (raw.CURRENCY.CURRENCY_SYMBOL || raw.CURRENCY.SYMBOL || raw.CURRENCY.symbol)) ||
      (raw.currencies &&
        (raw.currencies.CURRENCY_SYMBOL || raw.currencies.SYMBOL || raw.currencies.symbol)) ||
      "$";
    if (tx?.typeClass === "type-cashout") return `- ${formatted} ${symbol}`;
    if (tx?.typeClass === "type-cashin") return `+ ${formatted} ${symbol}`;
    return `${formatted} ${symbol}`;
  };

  const getAmountColorClass = (tx) => {
    if (tx?.typeClass === "type-cashin") return "transactions-amount--green";
    if (tx?.typeClass === "type-cashout") return "transactions-amount--red";
    return "transactions-amount--white";
  };

  const formatBalanceDisplay = (value) => {
    if (value == null || value === "") return "-";
    return `${value}$`;
  };

  const getIbStatusStyleKey = (tx) => {
    const statusCode = Number(tx?.statusCode);
    if (
      statusCode === TransactionStatus.COMPLETED ||
      statusCode === TransactionStatus.COLLECTED ||
      statusCode === TransactionStatus.TRANSFERRED
    ) {
      return "active";
    }

    if (
      statusCode === TransactionStatus.PENDING ||
      statusCode === TransactionStatus.PROGRESSING ||
      statusCode === TransactionStatus.STARTED ||
      statusCode === TransactionStatus.PENDING_USER
    ) {
      return "pending";
    }

    if (
      statusCode === TransactionStatus.FAILED ||
      statusCode === TransactionStatus.REJECTED ||
      statusCode === TransactionStatus.CANCELED
    ) {
      return "inactive";
    }

    const rawLabel = String(tx?.statusLabel || "").toLowerCase();
    if (
      rawLabel.includes("pending") ||
      rawLabel.includes("wait") ||
      rawLabel.includes("progress") ||
      rawLabel.includes("start")
    ) {
      return "pending";
    }
    if (
      rawLabel.includes("completed") ||
      rawLabel.includes("collected") ||
      rawLabel.includes("transferred") ||
      rawLabel.includes("success")
    ) {
      return "active";
    }
    if (
      rawLabel.includes("failed") ||
      rawLabel.includes("rejected") ||
      rawLabel.includes("cancel")
    ) {
      return "inactive";
    }

    return "inactive";
  };

  const getHistoryStatusPillClass = (tx) => {
    const key = getIbStatusStyleKey(tx);
    if (key === "active") return "history-status-pill--verified";
    if (key === "pending") return "history-status-pill--pending";
    if (key === "inactive" || key === "suspended") return "history-status-pill--rejected";
    return "history-status-pill--unknown";
  };

  const getHistoryStatusIconClass = (tx) => {
    const key = getIbStatusStyleKey(tx);
    if (key === "active") return "fa-solid fa-circle-check";
    if (key === "pending") return "fa-regular fa-clock";
    if (key === "inactive" || key === "suspended") return "fa-solid fa-circle-xmark";
    return "fa-regular fa-circle";
  };

  const getHistoryTypeStatusClass = (tx) => {
    if (tx?.typeClass === "type-cashin") return "ib-dashboard-status--active";
    if (tx?.typeClass === "type-cashout") return "ib-dashboard-status--inactive";
    if (tx?.typeClass === "type-transfer") return "ib-dashboard-status--pending";
    return "ib-dashboard-status--suspended";
  };

  return (
    <div className="accounts-list-page transactions-centered-page transactions-history-page">
      <Sidebar user={user} />
      <div className="accounts-list-content">
        <div className="transactions-history-filters">
          <div className="transactions-filter-bar w-100" aria-label="Filter transactions">
            <div className="transactions-filter-row">
              <div className="transactions-filter-field transactions-filter-field--search">
                <label>{t("search", { ns: "mobile" })}</label>
                <div className="transactions-filter-input-wrap">
                  <i className="fa-solid fa-magnifying-glass" aria-hidden />
                  <input
                    type="text"
                    className="transactions-filter-input"
                    placeholder={t("search_by_id_name_status", { ns: "mobile" })}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyPress}
                  />
                </div>
              </div>

              <div className="transactions-filter-field">
                <label>{t("type", { ns: "mobile" })}</label>
                <select
                  className="transactions-filter-select"
                  value={String(typeFilter)}
                  onChange={(e) => {
                    setTypeFilter(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  <option value={TransactionType.ALL}>{t("all", { ns: "mobile" })}</option>
                  <option value={TransactionType.CASHIN}>{t("cashin", { ns: "mobile" })}</option>
                  <option value={TransactionType.CASHOUT}>{t("cashout", { ns: "mobile" })}</option>
                  <option value={TransactionType.INTERNAL_TRANSFER}>{t("internal_transfer", { ns: "mobile" })}</option>
                </select>
              </div>

              <div className="transactions-filter-field">
                <label>From Date</label>
                <input
                  type="date"
                  className={`transactions-filter-input${
                    dateFieldErrors.dateFrom ? " transactions-filter-input--error" : ""
                  }`}
                  aria-invalid={dateFieldErrors.dateFrom}
                  value={toDateInputValue(dateRange.dateFrom)}
                  onChange={(e) => {
                    const dateFrom = parseLocalDateFromYmd(e.target.value);
                    setDateRange((p) => ({
                      ...p,
                      dateFrom,
                    }));
                    if (dateFrom) {
                      setDateFieldErrors((p) => ({ ...p, dateFrom: false }));
                    }
                  }}
                />
              </div>

              <div className="transactions-filter-field">
                <label>To Date</label>
                <input
                  type="date"
                  className={`transactions-filter-input${
                    dateFieldErrors.dateTo ? " transactions-filter-input--error" : ""
                  }`}
                  aria-invalid={dateFieldErrors.dateTo}
                  value={toDateInputValue(dateRange.dateTo)}
                  onChange={(e) => {
                    const dateTo = parseLocalDateFromYmd(e.target.value);
                    setDateRange((p) => ({
                      ...p,
                      dateTo,
                    }));
                    if (dateTo) {
                      setDateFieldErrors((p) => ({ ...p, dateTo: false }));
                    }
                  }}
                />
              </div>

              <div className="transactions-filter-actions">
                <button
                  type="button"
                  className={`transactions-filter-apply${
                    dateFieldErrors.dateFrom || dateFieldErrors.dateTo
                      ? " transactions-filter-apply--error"
                      : ""
                  }`}
                  aria-invalid={dateFieldErrors.dateFrom || dateFieldErrors.dateTo}
                  onClick={applyFilters}
                >
                  Filter
                </button>
                <button
                  type="button"
                  className="transactions-filter-reset"
                  onClick={() => {
                    setSearchTerm("");
                    setTypeFilter(TransactionType.ALL);
                    setDateRange({ dateFrom: null, dateTo: null, presetId: null });
                    setDateFieldErrors({ dateFrom: false, dateTo: false });
                    setPage(0);
                    TransactionHistoryController.clearCache();
                    loadTransactions({
                      page: 0,
                      type: TransactionType.ALL,
                      dateFrom: null,
                      dateTo: null,
                      search: null,
                    });
                  }}
                >
                  Reset
                </button>
                {canDownloadHistoryPdf ? (
                <div className="transactions-filter-more" ref={actionsMenuRef}>
                  <button
                    type="button"
                    className="transactions-filter-more-btn"
                    aria-label={t("ib_bulk_actions", { ns: "mobile" })}
                    aria-expanded={actionsMenuOpen}
                    aria-haspopup="menu"
                    aria-busy={isProcessingStatement}
                    disabled={isProcessingStatement}
                    onClick={() => {
                      if (isProcessingStatement) return;
                      setActionsMenuOpen((open) => !open);
                    }}
                  >
                    <i
                      className={
                        isProcessingStatement
                          ? "fa-solid fa-rotate fa-spin"
                          : "fa-solid fa-ellipsis-vertical"
                      }
                      aria-hidden
                    />
                  </button>
                  {actionsMenuOpen ? (
                    <div className="transactions-filter-more-menu" role="menu">
                      <button
                        type="button"
                        className="transactions-filter-more-item"
                        role="menuitem"
                        aria-label={t("email", { ns: "mobile" })}
                        onClick={handleEmailAction}
                        disabled={isProcessingStatement}
                      >
                        <i className="fa-regular fa-envelope" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="transactions-filter-more-item"
                        role="menuitem"
                        aria-label={t("download", { ns: "mobile" })}
                        onClick={handleDownloadAction}
                        disabled={isProcessingStatement}
                      >
                        <i className="fa-solid fa-download" aria-hidden />
                      </button>
                    </div>
                  ) : null}
                </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="accounts-table-container container-fluid px-0 d-flex flex-column">
          <div className="table-top-header">
            <div className="accounts-list-title-wrapper">
              <h1 className="accounts-list-title">{t("history", { ns: "mobile" })}</h1>
            </div>
          </div>
          <div className="table-responsive accounts-table-scroll">
          <div className="accounts-table transactions-table">
            <div className="table-header-row">
              <div className="table-header-cell">{t("transaction_id", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("transaction_date", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("type", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("amount", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("balance", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("status", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("action", { ns: "mobile" })}</div>
            </div>

            {isLoading &&
              Array.from({ length: 6 }).map((_, rowIndex) => (
                <div className="table-row" key={`sk-row-${rowIndex}`}>
                  {Array.from({ length: 7 }).map((__, colIndex) => (
                    <div className="table-cell" key={`sk-cell-${rowIndex}-${colIndex}`}>
                      <span className="accounts-skeleton-bar accounts-skeleton-bar--cell" />
                    </div>
                  ))}
                </div>
              ))}

            {!isLoading && error && (
              <div className="table-row no-data-row">
                <div className="table-cell no-data">{error}</div>
              </div>
            )}

            {!isLoading && !error && transactions.length === 0 && (
              <div className="table-row no-data-row">
                <div className="table-cell no-data">{t("no_content", { ns: "mobile" })}</div>
              </div>
            )}

            {!isLoading &&
              !error &&
              transactions.map((tx) => (
                <div className="table-row" key={tx.id || `${tx.date}-${Math.random()}`}>
                  <div className="table-cell">{tx.id || "-"}</div>
                  <div className="table-cell">{formatDateTime(tx.date)}</div>
                  <div className="table-cell">
                    <span className={`ib-dashboard-status ${getHistoryTypeStatusClass(tx)}`}>
                      <span className="ib-dashboard-status-dot" aria-hidden />
                      {tx.typeLabel || "Transaction"}
                    </span>
                  </div>
                  <div className="table-cell">
                    <span className={`transactions-amount ${getAmountColorClass(tx)}`}>
                      {tx.amount != null ? formatAmountDisplay(tx) : "-"}
                    </span>
                  </div>
                  <div className="table-cell">
                    {formatBalanceDisplay(tx.balanceAfter)}
                  </div>
                  <div className="table-cell">
                    <span className={`history-status-pill ${getHistoryStatusPillClass(tx)}`}>
                      <i className={getHistoryStatusIconClass(tx)} aria-hidden />
                      {tx.statusLabel || "-"}
                    </span>
                  </div>
                  <div className="table-cell">
                    <div className="request-action-buttons">
                      <button
                        className="request-action-btn request-action-btn--view"
                        aria-label="View details"
                        type="button"
                        onClick={() => openDetails(tx)}
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                      {isCashinType(tx) && shouldShowDocActions(tx) && (
                        <>
                          <button
                            className="request-action-btn request-action-btn--document"
                            aria-label="View uploaded documents"
                            type="button"
                            onClick={() => openCashinGatewayPopup(tx)}
                          >
                            <i className="fa-solid fa-file-lines"></i>
                          </button>
                          <button
                            className="request-action-btn request-action-btn--download"
                            aria-label="Download uploaded document"
                            type="button"
                            onClick={() => quickDownloadCashinDocument(tx)}
                          >
                            <i className="fa-solid fa-download"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
          </div>

          <div className="accounts-pagination accounts-pagination--opposite-dark">
            <div className="pagination-controls transactions-pagination-controls">
              <button
                className="pagination-btn"
                disabled={!canGoPrev}
                onClick={() => canGoPrev && setPage((prev) => Math.max(prev - 1, 0))}
              >
                <i className={`fa-solid ${isRtl ? "fa-chevron-right" : "fa-chevron-left"}`}></i>
              </button>
              <button className="pagination-btn active">
                <span>{page + 1}</span>
              </button>
              <button
                className="pagination-btn"
                disabled={!canGoNext}
                onClick={() => canGoNext && setPage((prev) => prev + 1)}
              >
                <i className={`fa-solid ${isRtl ? "fa-chevron-left" : "fa-chevron-right"}`}></i>
              </button>
            </div>
          </div>
        </div>
        <TransactionDetailsModal
          isOpen={isDetailsOpen}
          onClose={closeDetails}
          detailsTx={detailsTx}
          detailsData={detailsData}
          detailsLoading={detailsLoading}
          detailsError={detailsError}
        />
        {isGatewayOpen && (
          <div className="popup-overlay" onClick={closeGatewayPopup}>
            <div
              className="popup-content transactions-details-popup"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="transactions-details-header">
                <h2 className="transactions-details-title">
                  {t("uploaded_documents", {
                    ns: "mobile",
                    defaultValue: "Uploaded Documents",
                  })}
                </h2>
                <button
                  type="button"
                  className="transactions-details-close-btn"
                  onClick={closeGatewayPopup}
                  aria-label="Close"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
              <div className="transactions-details-body">
                <div className="transactions-details-row">
                  <span className="transactions-details-label">
                    {t("transaction_id", { ns: "mobile" })}
                  </span>
                  <span className="transactions-details-value">
                    {gatewayTx?.id || "-"}
                  </span>
                </div>
                {gatewayLoading && (
                  <div className="transactions-details-row">
                    <span className="transactions-details-label">
                      {t("loading", { ns: "mobile" })}
                    </span>
                  </div>
                )}
                {!gatewayLoading && gatewayError && (
                  <div className="transactions-details-row">
                    <span className="transactions-details-label">
                      {t("error", { ns: "mobile" })}
                    </span>
                    <span className="transactions-details-value">{gatewayError}</span>
                  </div>
                )}
                {!gatewayLoading && !gatewayError && gatewayDocuments.length === 0 && (
                  <div className="transactions-details-row">
                    <span className="transactions-details-label">
                      {t("no_content", { ns: "mobile" })}
                    </span>
                  </div>
                )}
                {!gatewayLoading &&
                  !gatewayError &&
                  gatewayDocuments.map((filePath) => (
                    <div className="transactions-details-row" key={filePath}>
                      <span className="transactions-details-label">
                        {t("document", { ns: "mobile", defaultValue: "Document" })}
                      </span>
                      <span className="transactions-details-value">
                        {String(filePath).split("/").pop()}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
        {/* Inline filter bar replaces modal DateRangeFilter */}
      </div>
      <TransactionPasswordDialog
        open={showDownloadPassword}
        mode="verify"
        title={
          statementAction === "email"
            ? t("confirm_email", { ns: "mobile", defaultValue: "Confirm email" })
            : t("confirm_download", { ns: "mobile", defaultValue: "Confirm download" })
        }
        firstMessage={
          statementAction === "email"
            ? t("enter_password_to_email", {
                ns: "mobile",
                defaultValue: "Enter your password to send the statement email.",
              })
            : t("enter_password_to_download", {
                ns: "mobile",
                defaultValue: "Enter your password to download the statement.",
              })
        }
        onClose={handleDownloadPasswordClose}
      />
    </div>
  );
};

export default TransactionsHistory;

